import { useEffect, useRef, useState } from "react";
import { ApiError } from "@repo/api-client";
import type { StaffBookingSummaryResponse } from "@repo/api-client";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useVenue } from "../../shell/VenueContext";
import { Overlay } from "../../bookings/ManualEntryDialog";
import {
  banner,
  dangerButton,
  fieldLabel,
  primaryButton,
  secondaryButton,
  textInput,
} from "../../bookings/styles";
import { formatFee } from "./noShowChargeApi";
import { mintNoShowChargeStepUp, useChargeNoShow, useGuarantee, useRefundNoShow } from "./queries";

/** Map a charge/refund `ApiError` to the pre-translated no-show-charge message (ADR-0008). */
function messageForError(err: unknown, t: (k: string) => string): string {
  if (err instanceof ApiError && err.problem) {
    const code = (err.problem as { errorCode?: string }).errorCode ?? "";
    if (code === "STEP_UP_REQUIRED") return t("app.noShowCharge.stepUpRequired");
    if (code === "ALREADY_CHARGED") return t("app.noShowCharge.alreadyCharged");
    if (code === "NOT_ELIGIBLE") return t("app.noShowCharge.notEligible");
  }
  // A declined credential (expired/cancelled/non-rechargeable) is recorded FAILED — no auto-retry (R10).
  return t("app.noShowCharge.failed");
}

/**
 * The manager no-show charge / refund dialog (card-fingerprint-no-show.md R4/R6/R10). It shows the FIXED
 * fee (never an editable amount, R4) + the card ending/brand, and takes a manager PIN that mints a
 * single-use `NO_SHOW_CHARGE` step-up (ST-4) which rides on the charge. A declined credential is recorded
 * as a FAILED charge with no retry (R10); a `STEP_UP_REQUIRED`/`ALREADY_CHARGED`/`NOT_ELIGIBLE` problem is
 * mapped to its message. An already-charged guarantee instead offers a refund (wrongly-triggered → REFUNDED).
 */
export function NoShowChargeDialog({
  booking,
  onClose,
  onDone,
}: {
  booking: StaffBookingSummaryResponse;
  onClose: () => void;
  onDone: () => void;
}) {
  const { venueId } = useVenue();
  const { t, i18n } = useTranslation();
  const bookingId = booking.bookingId ?? "";
  const g = useGuarantee(venueId, bookingId);
  const charge = useChargeNoShow(venueId);
  const refund = useRefundNoShow(venueId);
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => headingRef.current?.focus(), []);

  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<"charged" | "refunded" | null>(null);

  const guarantee = g.data;
  const chargeState = (guarantee?.chargeState ?? "NONE").toUpperCase();
  const feeText =
    guarantee && guarantee.feeMinor !== undefined && guarantee.currency
      ? formatFee(guarantee.feeMinor, guarantee.currency, i18n.language)
      : "";
  const last4 = guarantee?.cardLast4 ?? "";
  const brand = guarantee?.cardBrand ?? "";

  async function doCharge() {
    setError(null);
    setBusy(true);
    let authId: string;
    try {
      const auth = await mintNoShowChargeStepUp(venueId, pin, bookingId);
      authId = auth.authorizationId;
    } catch {
      setBusy(false);
      // Wrong/no PIN → nothing is sent to the charge endpoint (R6 no-PIN/wrong-PIN edge).
      setError(t("app.noShowCharge.stepUpRequired"));
      return;
    }
    charge.mutate(
      { bookingId, stepUpAuthId: authId },
      {
        onSettled: () => setBusy(false),
        onSuccess: () => {
          setResult("charged");
          onDone();
        },
        onError: (err) => setError(messageForError(err, t)),
      },
    );
  }

  function doRefund() {
    const reason = window.prompt(t("app.noShowCharge.refund"));
    if (!reason) return; // A refund without a reason is rejected — never proceed.
    setError(null);
    setBusy(true);
    refund.mutate(
      { bookingId, reason },
      {
        onSettled: () => setBusy(false),
        onSuccess: () => {
          setResult("refunded");
          onDone();
        },
        onError: (err) => setError(messageForError(err, t)),
      },
    );
  }

  return (
    <Overlay onClose={onClose} label={t("app.noShowCharge.confirmTitle")}>
      <h2
        ref={headingRef}
        tabIndex={-1}
        className={css({ fontSize: "lg", fontWeight: "bold", outline: "none" })}
      >
        {t("app.noShowCharge.confirmTitle")}
      </h2>

      {error ? (
        <p role="alert" className={banner("error")}>
          {error}
        </p>
      ) : null}

      {result === "charged" ? (
        <ResultView
          message={t("app.noShowCharge.charged")}
          onClose={onClose}
          closeLabel={t("bookings.action.back")}
        />
      ) : result === "refunded" ? (
        <ResultView
          message={t("app.noShowCharge.refunded")}
          onClose={onClose}
          closeLabel={t("bookings.action.back")}
        />
      ) : g.isLoading ? (
        <p>{t("deposits.loading")}</p>
      ) : chargeState === "CHARGED" ? (
        // Wrongly-triggered charge → refund path (never silently ignored).
        <div className={css({ display: "flex", flexDirection: "column", gap: "3" })}>
          {last4 ? (
            <p data-testid="noshow-card" className={css({ fontSize: "sm" })}>
              {t("app.noShowCharge.card", { last4, brand })}
            </p>
          ) : null}
          <div className={actions}>
            <button type="button" className={secondaryButton} onClick={onClose}>
              {t("bookings.action.back")}
            </button>
            <button
              type="button"
              className={dangerButton}
              disabled={busy}
              onClick={doRefund}
              data-testid="noshow-refund-submit"
            >
              {busy ? t("bookings.action.saving") : t("app.noShowCharge.refund")}
            </button>
          </div>
        </div>
      ) : chargeState === "FAILED" ? (
        <p data-testid="noshow-already-failed">{t("app.noShowCharge.failed")}</p>
      ) : chargeState === "REFUNDED" ? (
        <p data-testid="noshow-already-refunded">{t("app.noShowCharge.refunded")}</p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void doCharge();
          }}
          className={css({ display: "flex", flexDirection: "column", gap: "3" })}
        >
          <p data-testid="noshow-confirm-body">
            {t("app.noShowCharge.confirmBody", { fee: feeText, last4 })}
          </p>
          {/* R4: the amount is FIXED by the booking's terms — displayed as text, never an editable field. */}
          <p data-testid="noshow-fee" className={css({ fontSize: "sm", fontWeight: "medium" })}>
            {feeText}
          </p>
          <p className={css({ fontSize: "xs", color: "fg.muted" })}>
            {t("app.noShowCharge.amountFixed")}
          </p>
          {last4 ? (
            <p data-testid="noshow-card" className={css({ fontSize: "sm", color: "fg.muted" })}>
              {t("app.noShowCharge.card", { last4, brand })}
            </p>
          ) : null}

          <label className={fieldLabel}>
            {t("app.noShowCharge.pinLabel")}
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              className={textInput}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              data-testid="noshow-pin"
            />
          </label>

          <div className={actions}>
            <button type="button" className={secondaryButton} onClick={onClose}>
              {t("bookings.action.back")}
            </button>
            <button
              type="submit"
              className={primaryButton}
              disabled={busy || pin.trim() === ""}
              data-testid="noshow-charge-submit"
            >
              {busy ? t("bookings.action.saving") : t("app.noShowCharge.submit")}
            </button>
          </div>
        </form>
      )}
    </Overlay>
  );
}

function ResultView({
  message,
  onClose,
  closeLabel,
}: {
  message: string;
  onClose: () => void;
  closeLabel: string;
}) {
  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "3" })}>
      <output data-testid="noshow-result">{message}</output>
      <div className={actions}>
        <button type="button" className={primaryButton} onClick={onClose}>
          {closeLabel}
        </button>
      </div>
    </div>
  );
}

const actions = css({ display: "flex", gap: "2", justifyContent: "flex-end", flexWrap: "wrap" });
